/**
 * Bookkeeping for concurrent role permission loads.
 *
 * Two loads can be in flight at once: the bootstrap fires one and the user picks a different
 * role a moment later. Responses can land out of order, so the later request wins and every
 * earlier one is discarded. No imports, so this is unit testable on its own.
 */

let latestRequestToken = 0;

/**
 * Claim the newest request slot, invalidating every load already in flight
 * @returns The token this load must present when it finishes
 */
export const nextRequestToken = (): number => {
  latestRequestToken += 1;
  return latestRequestToken;
};

/**
 * Invalidate every load in flight without starting a new one
 */
export const invalidateInFlightLoads = (): void => {
  latestRequestToken += 1;
};

/** The token of the most recently started load. Exposed for tests. */
export const getLatestRequestToken = (): number => latestRequestToken;

export interface StaleLoadInput {
  /** Token the finishing load was started with. */
  token: number;
  /** Token of the most recently started load. */
  latestToken: number;
  /** Role the finishing load asked for. */
  requestedRoleId: string;
  /** Role selected right now. */
  currentRoleId: string | null;
}

/**
 * Whether a finished load's result must be thrown away
 * @param input The finishing load's token and role, against the current ones
 * @returns True when a newer load superseded it, or the selected role has since changed
 */
export const isStaleLoad = (input: StaleLoadInput): boolean =>
  input.token !== input.latestToken || input.currentRoleId !== input.requestedRoleId;
